import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import { Template, saveTemplate, getUserTemplates, deleteTemplate } from '../services/templates';

interface TemplateManagerProps {
  currentHtml: string;
  onTemplateSelect: (html: string) => void;
  isPro: boolean;
  onUpgradeClick?: () => void;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
  currentHtml,
  onTemplateSelect,
  isPro,
  onUpgradeClick
}) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user && isPro) {
      loadTemplates();
    }
  }, [user, isPro]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const userTemplates = await getUserTemplates();
      setTemplates(userTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    if (!user) {
      setError('Please sign in to save templates');
      return;
    }
    if (!isPro) {
      if (onUpgradeClick) {
        onUpgradeClick();
      }
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleSaveConfirm = async () => {
    try {
      setLoading(true);
      const newTemplate = await saveTemplate({
        name: templateName,
        html: currentHtml,
        category: templateCategory,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      
      setTemplates([newTemplate, ...templates]);
      setSaveDialogOpen(false);
      setTemplateName('');
      setTemplateCategory('');
      setSuccess('Template saved successfully');
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template: Template) => {
    onTemplateSelect(template.html);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, template: Template) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = async () => {
    if (selectedTemplate) {
      try {
        setLoading(true);
        await deleteTemplate(selectedTemplate.id);
        setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
        handleMenuClose();
        setSuccess('Template deleted successfully');
      } catch (err) {
        console.error('Error deleting template:', err);
        setError('Failed to delete template');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportTemplate = () => {
    if (selectedTemplate) {
      const blob = new Blob([selectedTemplate.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTemplate.name}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      handleMenuClose();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Templates</Typography>
        <Button
          startIcon={isPro ? <SaveIcon /> : <LockIcon />}
          variant="contained"
          onClick={handleSaveClick}
          disabled={loading}
          color={isPro ? 'primary' : 'secondary'}
        >
          {isPro ? 'Save Template' : 'Pro Feature'}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isPro ? (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'action.hover',
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Template Management
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upgrade to Pro to save and manage your email templates
          </Typography>
          {onUpgradeClick && (
            <Button
              variant="contained"
              color="primary"
              onClick={onUpgradeClick}
              sx={{ mt: 1 }}
            >
              Upgrade to Pro
            </Button>
          )}
        </Paper>
      ) : (
        <List>
          {templates.map((template) => (
            <ListItem
              key={template.id}
              button
              onClick={() => handleTemplateClick(template)}
            >
              <ListItemText
                primary={template.name}
                secondary={`Category: ${template.category || 'Uncategorized'}`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={(e) => handleMenuClick(e, template)}
                >
                  <MoreVertIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {!loading && templates.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No templates saved"
                secondary="Save your first template to see it here"
              />
            </ListItem>
          )}
        </List>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Category (optional)"
            fullWidth
            value={templateCategory}
            onChange={(e) => setTemplateCategory(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfirm}
            variant="contained"
            disabled={!templateName || loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleExportTemplate}>
          <FileDownloadIcon sx={{ mr: 1 }} /> Export
        </MenuItem>
        <MenuItem onClick={handleDeleteTemplate} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 